import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useReportingTemplateStore } from "../../../../config/stores";
import AppInput from "../../../common/AppInput";
import { Divider, Form, Row } from "antd";
import { find, isEmpty, map } from "lodash";
import AppButton from "../../../common/AppButton";
import dayjs from "dayjs";
import advanceAccountabilityFormThunks from "../../../../config/thunks/finance/advanceAccountabilityForm/advanceAccountabilityForm.thunks";
import AntdTable from "../../../common/AntdTable";

const ActivityReportingTemplate = ({ showSubmitButton = true }) => {
  const { advanceAccountabilityFormsDetails } = useSelector(
    (state) => state.advanceAccountabilityFormsState
  );
  const reportingTemplateStore = useReportingTemplateStore();

  const reportingTemplate = useMemo(
    () => advanceAccountabilityFormsDetails.reportingTemplate || {},
    [advanceAccountabilityFormsDetails.reportingTemplate]
  );

  const [form] = Form.useForm();

  const indicators = Form.useWatch("indicator_id", form);

  const mu_0_12    = Form.useWatch("male_ugandan_0_12", form);
  const mu_13_19   = Form.useWatch("male_ugandan_13_19", form);
  const mu_20_35   = Form.useWatch("male_ugandan_20_35", form);
  const mu_above35 = Form.useWatch("male_ugandan_above_35", form);
  const fu_0_12    = Form.useWatch("female_ugandan_0_12", form);
  const fu_13_19   = Form.useWatch("female_ugandan_13_19", form);
  const fu_20_35   = Form.useWatch("female_ugandan_20_35", form);
  const fu_above35 = Form.useWatch("female_ugandan_above_35", form);
  const mr_0_12    = Form.useWatch("male_refugee_0_12", form);
  const mr_13_19   = Form.useWatch("male_refugee_13_19", form);
  const mr_20_35   = Form.useWatch("male_refugee_20_35", form);
  const mr_above35 = Form.useWatch("male_refugee_above_35", form);
  const fr_0_12    = Form.useWatch("female_refugee_0_12", form);
  const fr_13_19   = Form.useWatch("female_refugee_13_19", form);
  const fr_20_35   = Form.useWatch("female_refugee_20_35", form);
  const fr_above35 = Form.useWatch("female_refugee_above_35", form);
  const disabilityMale   = Form.useWatch("persons_with_disability_male", form);
  const disabilityFemale = Form.useWatch("persons_with_disability_female", form);

  const n = (v) => Number(v) || 0;
  const maleUgandanTotal   = n(mu_0_12) + n(mu_13_19) + n(mu_20_35) + n(mu_above35);
  const femaleUgandanTotal = n(fu_0_12) + n(fu_13_19) + n(fu_20_35) + n(fu_above35);
  const maleRefugeeTotal   = n(mr_0_12) + n(mr_13_19) + n(mr_20_35) + n(mr_above35);
  const femaleRefugeeTotal = n(fr_0_12) + n(fr_13_19) + n(fr_20_35) + n(fr_above35);
  const disabilityTotal    = n(disabilityMale) + n(disabilityFemale);
  const total_0_12    = n(mu_0_12) + n(fu_0_12) + n(mr_0_12) + n(fr_0_12);
  const total_13_19   = n(mu_13_19) + n(fu_13_19) + n(mr_13_19) + n(fr_13_19);
  const total_20_35   = n(mu_20_35) + n(fu_20_35) + n(mr_20_35) + n(fr_20_35);
  const total_above35 = n(mu_above35) + n(fu_above35) + n(mr_above35) + n(fr_above35);
  const grandTotal    = maleUgandanTotal + femaleUgandanTotal + maleRefugeeTotal + femaleRefugeeTotal;

  useEffect(() => {
    form.setFieldsValue({
      ...reportingTemplate,
      indicator_id: map(
        reportingTemplate?.activityReportingTemplateIndicators,
        "indicator_id"
      ),
      indicators: map(
        reportingTemplate?.activityReportingTemplateIndicators,
        (indicator) => ({
          indicator_id: indicator.indicator_id,
          score: indicator.score,
        })
      ),
      date: dayjs(reportingTemplate?.date),
    });
  }, [reportingTemplate, form]);

  const dispatch = useDispatch();

  const reloadAdvanceForm = () => {
    dispatch(
      advanceAccountabilityFormThunks.getAdvanceAccountabilityFormDetails(
        advanceAccountabilityFormsDetails.id
      )
    );
  };
  //

  const onSave = async (values) => {
    values.project_id = advanceAccountabilityFormsDetails.project;
    values.concept_note_id = advanceAccountabilityFormsDetails?.conceptNote?.id;
    values.payment_voucher_id = advanceAccountabilityFormsDetails.id;
    if (!isEmpty(reportingTemplate)) {
      await reportingTemplateStore.updateReportingTemplate(
        reportingTemplate.id,
        values,
        reloadAdvanceForm
      );
    } else {
      await reportingTemplateStore.createReportingTemplate(
        values,
        reloadAdvanceForm
      );
    }
  };

  useEffect(() => {
    if (!reportingTemplate) {
      form.setFieldValue(
        "indicators",
        map(indicators, (indictor) => ({
          indicator_id: indictor,
          score: 0,
        }))
      );
    } else {
      const selectedIndicators = map(indicators, (indicator) => {
        const hasValue = find(
          reportingTemplate?.activityReportingTemplateIndicators,
          { indicator_id: indicator }
        );

        if (hasValue)
          return {
            indicator_id: hasValue.indicator_id,
            score: hasValue.score,
          };
        else
          return {
            indicator_id: indicator,
            score: 0,
          };
      });
      form.setFieldValue("indicators", selectedIndicators);
    }
  }, [
    indicators,
    reportingTemplate,
    form,
    advanceAccountabilityFormsDetails.strategicPlanIndicators,
  ]);

  return (
    <div>
      <p className="text-center h5">Amani Initiative Activity Report</p>
      <Form
        form={form}
        onFinish={onSave}
        disabled={
          advanceAccountabilityFormsDetails?.reportingTemplate?.status ===
          "approved"
        }
      >
        <table className="table table-sm table-bordered">
          <tbody>
            <tr>
              <td className="w-50 fw-bold">Project/initiative name</td>
              <td>
                {advanceAccountabilityFormsDetails.projectDetails?.project_name}
              </td>
            </tr>
            <tr>
              <td className="w-50 fw-bold">Title of activity</td>
              <td>
                {advanceAccountabilityFormsDetails?.budgetLineDetails?.name}
              </td>
            </tr>
            {/* <tr>
              <td className="w-50 fw-bold">Strategic plan priority Input</td>
              <td>{advanceAccountabilityFormsExpenditureDetails.name}</td>
            </tr> */}
            <tr>
              <td className="w-50 fw-bold">Strategic plan indicator</td>
              <td>
                <AppInput
                  type="select"
                  name="indicator_id"
                  inputAttributes={{
                    variant: "borderless",
                    placeholder: "You can select the one that apply.",
                    mode: "multiple",
                  }}
                  options={map(
                    advanceAccountabilityFormsDetails.strategicPlanIndicators,
                    (indicator) => ({
                      label: indicator.indicator,
                      value: indicator.id,
                    })
                  )}
                />
              </td>
            </tr>

            <Form.List name="indicators">
              {(fields) => (
                <>
                  {fields.map((field, index) => (
                    <tr key={field.key}>
                      <td>
                        <AppInput
                          type="select"
                          name={[field.name, "indicator_id"]}
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "You can select the one that apply.",
                            readOnly: true,
                          }}
                          options={map(
                            advanceAccountabilityFormsDetails.strategicPlanIndicators,
                            (indicator) => ({
                              label: indicator.indicator,
                              value: indicator.id,
                              disabled: true,
                            })
                          )}
                        />
                      </td>
                      <td>
                        <AppInput
                          type="number"
                          name={[field.name, "score"]}
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "enter a value",
                            min: 0,
                          }}
                        />
                      </td>
                    </tr>
                  ))}{" "}
                </>
              )}
            </Form.List>
            <tr>
              <td className="w-50 fw-bold">Funder</td>
              <td>
                {advanceAccountabilityFormsDetails?.projectDetails?.donor_name}
              </td>
            </tr>
            <tr>
              <td className="w-50 fw-bold">
                Dates and duration{" "}
                <span className="text-muted fw-normal">
                  (Indicate date and how long the activity took place)
                </span>{" "}
              </td>
              <td>
                <Row>
                  <span className="mx-2">Start Date:</span>{" "}
                  <AppInput
                    type="date"
                    name="date"
                    inputAttributes={{
                      variant: "borderless",
                    }}
                  />
                  <span className="mx-2"> Duration:</span>
                  <AppInput
                    type="number"
                    name="duration"
                    inputAttributes={{
                      variant: "borderless",
                      placeholder: "Enter Period",
                    }}
                  />
                  <AppInput
                    type="select"
                    name="duration_unit"
                    inputAttributes={{
                      variant: "borderless",
                      placeholder: "Select Period Type",
                    }}
                    options={[
                      { label: "Hours(s)", value: "hours" },
                      { label: "Day(s)", value: "days" },
                      { label: "Week(s)", value: "weeks" },
                      { label: "Month(s)", value: "months" },
                    ]}
                  />
                </Row>
              </td>
            </tr>
            <tr>
              <td className="fw-bold">
                Activity Venue{" "}
                <span className="text-muted fw-normal">
                  (Indicate the venue including district, sub-county, and
                  villages in which the activity took place)
                </span>
              </td>
              <td>
                <AppInput
                  name="venue"
                  inputAttributes={{
                    variant: "borderless",
                    placeholder: "Enter Venue",
                  }}
                />
              </td>
            </tr>
            <tr>
              <td className="fw-bold">
                Description of the activity{" "}
                <span className="text-muted fw-normal">
                  (Give a summary description including activity objectives, and
                  how (methodology or approach) this activity was conducted or
                  carried out.)
                </span>
              </td>
              <td>
                <AppInput
                  type="bullets"
                  name="achievements_and_results"
                  inputAttributes={{
                    variant: "borderless",
                    placeholder: "Enter Achievements and results",
                    autoSize: { minRows: 2 },
                  }}
                />
              </td>
            </tr>
            <tr>
              <td className="fw-bold">
                Activity achievements and results{" "}
                <span className="text-muted fw-normal">
                  ( Provide the successes/results of the activity in relation to
                  what was planned. Also describe the unplanned
                  successes/results of the activity. (Include both the outcome
                  and output results. Outcomes refer to changes in capacities of
                  project participants or intermediaries for instance 10%
                  increase in knowledge, positive change in attitude Outputs
                  refer to: a))
                </span>
              </td>
              <td>
                <AppInput
                  type="bullets"
                  name="description"
                  inputAttributes={{
                    variant: "borderless",
                    placeholder: `Enter  Activity Achievements`,
                    autoSize: { minRows: 3 },
                  }}
                />
              </td>
            </tr>
            <tr>
              <td className="fw-bold">
                Challenges encountered and how these challenges were overcome{" "}
                <span className="text-muted fw-normal">
                  (If any, provide the technical and operational factors that
                  affected implementation of the activity and solutions that
                  were implored to address these challenges)
                </span>
              </td>
              <td>
                <AppInput
                  type="bullets"
                  name="challenges"
                  inputAttributes={{
                    variant: "borderless",
                    placeholder: `Enter  Challenges`,
                    autoSize: { minRows: 2 },
                  }}
                />
              </td>
            </tr>
            <tr>
              <td className="fw-bold">
                Key lessons{" "}
                <span className="text-muted fw-normal">
                  ( Explicitly describe the key lessons learned from the
                  implementation of this activity These lessons should be able
                  to guide future activities and programming)
                </span>
              </td>
              <td>
                <AppInput
                  type="bullets"
                  name="key_lessons"
                  inputAttributes={{
                    variant: "borderless",
                    placeholder: `Enter  Key Lessons`,
                    autoSize: { minRows: 2 },
                  }}
                />
              </td>
            </tr>
            <tr>
              <td className="fw-bold">
                Recommendations{" "}
                <span className="text-muted fw-normal">
                  (Give a summary description of what do we needs to be done
                  better to ensure the activity achieves its intended
                  objectives)
                </span>
              </td>
              <td>
                <AppInput
                  type="bullets"
                  name="recommendations"
                  inputAttributes={{
                    variant: "borderless",
                    placeholder: `Enter Recommendations`,
                    autoSize: { minRows: 2 },
                  }}
                />
              </td>
            </tr>
            <tr>
              <td className="fw-bold">
                Number of project participants or intermediaries reached{" "}
                <span className="text-muted fw-normal">
                  (This is to automatically update Amani Initiative’s strategic
                  plan indicators and performance based on the selected activity
                  and strategic plan priority in put selected)
                </span>
              </td>
              <td>
                <table class="table table-bordered table-sm ">
                  <thead class="">
                    <tr>
                      <th>Sex</th>
                      <th>0-12</th>
                      <th>13-19</th>
                      <th>20-35</th>
                      <th>Above 35</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Male Ugandan</td>
                      <td>
                        <AppInput
                          type="number"
                          name="male_ugandan_0_12"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                            min: 0,
                          }}
                        />
                      </td>
                      <td>
                        <AppInput
                          type="number"
                          name="male_ugandan_13_19"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                            min: 0,
                          }}
                        />
                      </td>
                      <td>
                        <AppInput
                          type="number"
                          name="male_ugandan_20_35"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                            min: 0,
                          }}
                        />
                      </td>
                      <td>
                        <AppInput
                          type="number"
                          name="male_ugandan_above_35"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                            min: 0,
                          }}
                        />
                      </td>
                      <td className="fw-bold text-center align-middle">{maleUgandanTotal}</td>
                    </tr>
                    <tr>
                      <td>Female Ugandan</td>
                      <td>
                        <AppInput
                          type="number"
                          name="female_ugandan_0_12"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                            min: 0,
                          }}
                        />
                      </td>
                      <td>
                        <AppInput
                          type="number"
                          name="female_ugandan_13_19"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                          }}
                        />
                      </td>
                      <td>
                        <AppInput
                          type="number"
                          name="female_ugandan_20_35"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                          }}
                        />
                      </td>
                      <td>
                        <AppInput
                          type="number"
                          name="female_ugandan_above_35"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                            min: 0,
                          }}
                        />
                      </td>
                      <td className="fw-bold text-center align-middle">{femaleUgandanTotal}</td>
                    </tr>
                    <tr>
                      <td>Male Refugee</td>
                      <td>
                        <AppInput
                          type="number"
                          name="male_refugee_0_12"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                            min: 0,
                          }}
                        />
                      </td>
                      <td>
                        <AppInput
                          type="number"
                          name="male_refugee_13_19"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                            min: 0,
                          }}
                        />
                      </td>
                      <td>
                        <AppInput
                          type="number"
                          name="male_refugee_20_35"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                            min: 0,
                          }}
                        />
                      </td>
                      <td>
                        <AppInput
                          type="number"
                          name="male_refugee_above_35"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                            min: 0,
                          }}
                        />
                      </td>
                      <td className="fw-bold text-center align-middle">{maleRefugeeTotal}</td>
                    </tr>
                    <tr>
                      <td>Female Refugee</td>
                      <td>
                        <AppInput
                          type="number"
                          name="female_refugee_0_12"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                          }}
                        />
                      </td>
                      <td>
                        <AppInput
                          type="number"
                          name="female_refugee_13_19"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                          }}
                        />
                      </td>
                      <td>
                        <AppInput
                          type="number"
                          name="female_refugee_20_35"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                          }}
                        />
                      </td>
                      <td>
                        <AppInput
                          type="number"
                          name="female_refugee_above_35"
                          inputAttributes={{
                            variant: "borderless",
                            placeholder: "eg 2",
                          }}
                        />
                      </td>
                      <td className="fw-bold text-center align-middle">{femaleRefugeeTotal}</td>
                    </tr>
                    <tr className="fw-bold">
                      <td>Total</td>
                      <td className="text-center align-middle">{total_0_12}</td>
                      <td className="text-center align-middle">{total_13_19}</td>
                      <td className="text-center align-middle">{total_20_35}</td>
                      <td className="text-center align-middle">{total_above35}</td>
                      <td className="text-center align-middle">{grandTotal}</td>
                    </tr>
                  </tbody>
                </table>
                <Divider>Person with a disability</Divider>
                <table className="table table-sm table-bordered">
                  <tr>
                    <td>Male</td>
                    <td>
                      <AppInput
                        type="number"
                        name="persons_with_disability_male"
                        inputAttributes={{
                          variant: "borderless",
                          placeholder: "eg 2",
                          min: 0,
                        }}
                      />
                    </td>
                    <td>Female</td>
                    <td>
                      {" "}
                      <AppInput
                        type="number"
                        name="persons_with_disability_female"
                        inputAttributes={{
                          variant: "borderless",
                          placeholder: "eg 2",
                          min: 0,
                        }}
                      />
                    </td>
                    <td>Total</td>
                    <td className="fw-bold text-center align-middle">{disabilityTotal}</td>
                  </tr>
                </table>
              </td>
            </tr>
            <tr>
              <td className="fw-bold">
                Community Voice (Quotes){" "}
                <span className="text-muted fw-normal">
                  Community Voice (Quotes){" "}
                </span>
              </td>
              <td>
                <AppInput
                  type="textarea"
                  name="community_voice"
                  inputAttributes={{
                    variant: "borderless",
                    placeholder: `Enter Community Voice (Quotes)`,
                    autoSize: { minRows: 2 },
                  }}
                />
              </td>
            </tr>
            <tr>
              <td className="fw-bold">
                Success Stories{" "}
                <span className="text-muted fw-normal">
                  Success Stories{" "}
                </span>
              </td>
              <td>
                <AppInput
                  type="textarea"
                  name="success_story"
                  inputAttributes={{
                    variant: "borderless",
                    placeholder: `Enter Success Stories`,
                    autoSize: { minRows: 2 },
                  }}
                />
              </td>
            </tr>
          </tbody>
        </table>

        {!isEmpty(reportingTemplate) &&
          !isEmpty(reportingTemplate?.actionPoints) && (
            <>
              <AntdTable
                title={() => (
                  <Divider className="text-uppercase fw-bold">
                    Activity follow up and action points{" "}
                  </Divider>
                )}
                data={reportingTemplate?.actionPoints}
                columns={[
                  {
                    title: "ACTION POINT",
                    dataIndex: "action_point",
                  },
                  {
                    title: "PERSON RESPONSIBLE",
                    dataIndex: "person_responsible",
                  },
                  {
                    title: "TIMELINE",
                    dataIndex: "timeline",
                  },
                  {
                    title: "STATUS",
                    dataIndex: "status",
                  },
                  {
                    title: "ACTION",
                    dataIndex: "action",
                  },
                ]}
              />
            </>
          )}

        {showSubmitButton && (
          <AppButton
            text="SAVE ACTIVITY REPORT"
            type="submit"
            block
            disabled={
              reportingTemplate.status === "approved" ||
              advanceAccountabilityFormsDetails.status === "approved"
            }
            loadingText="SAVING..."
            className="mt-3"
            loading={
              reportingTemplateStore.creatingReportTemplate ||
              reportingTemplateStore.updatingReportTemplate
            }
          />
        )}
      </Form>
    </div>
  );
};

export default ActivityReportingTemplate;
